import { useTranslation } from "react-i18next"
import WhiteSpace from "../../components/Whitespace"
import PageTitle from "../../components/PageTitle"
import ChargerType from "../../types/ChargerType"
import styled from "styled-components"
import { zeroLineHeight, wrapWhenNarrow } from '../../styles/mixins'
import Icon from "../../Icon"
import dateToTimeAgoString from "../../utils/dateToTimeAgoString"
import Select from "../../components/Select"
import Dropdown from "../../components/Dropdown"
import { useEffect, useState } from "react"
import { child, get, getDatabase, ref, set } from "firebase/database"
import fbConf from "../../FirebaseConf"
import Comm from "../../Comm"
import { useNotification } from "../../modules/Notification"
import { useNavigate } from "react-router-dom"
import LoaderDots from "../../components/LoaderDots"
import { BREAKPOINTS, TAP_MIN } from "../../GlobalTheme"

interface ChargerGroupChargerType {
  fwVersion: string,
  osVersion: string,
  swVersion: string,
  fwVersionNumber: string,
  osVersionNumber: string,
  swVersionNumber: string,
  userEmail: string,
  online: boolean,
  lastPresence: string,
  localName: string,
}

interface ChargerGroupType {
  chargers: {
    [key: string]: ChargerGroupChargerType
  },
  groupFwVersion: string,
  groupOsVersion: string,
  groupSwVersion: string,
  lastEdit?: string,
  frontendFilter?: { SW?: string, FW?: string, OS?: string }
}

type VersionKey = 'swVersion' | 'fwVersion' | 'osVersion'

const GroupsContainer = styled.div`
  width: 100%;
  height: calc(90vh - 170px);
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    max-height: calc(100dvh - 160px);
    overscroll-behavior: contain;
  }
  overflow-y: scroll;
`

const GroupContainer = styled.div`
  width: calc(100% - 30px - 16px - 39px);
  margin-left: 25px;
  margin-bottom: 8px;
  background-color: ${props => props.theme.colors.white};
  border-radius: 24px;
  padding-top: 10px;
  padding-left: 39px;
  /* Reserves the strip used by the absolutely positioned group-edit button at right:40px. */
  padding-right: 70px;
  /* Without this the width above is the CONTENT width, so the padding reserved for the buttons is
     ADDED to the card - pushing the card, and the buttons with it, past the viewport. */
  box-sizing: border-box;
  position: relative;

  /* 25px margin + 39px padding + an 85px width deduction leaves ~240px of content on a phone. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    margin-left: 0px;
    padding-left: 16px;
    padding-right: 16px;
  }
`

const GroupHeaderTable = styled.table`
  /* As a real table the used width is max(specified, min-content), and the flex row's non-shrinking
     percentage cells make min-content far wider than the card - so the row overflowed the card and
     ran under the edit button. As a block the 98% is honoured and the percentages have a real base. */
  display: block;
  width: 98%;

  > tbody {
    display: block;
  }
`

const GroupEditButton = styled.div`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 28px;
  right: 40px;
  cursor: pointer;

  /* A 16px icon is not a touch target. Grown to 44px and re-anchored so the icon stays put. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: ${TAP_MIN}px;
    height: ${TAP_MIN}px;
    top: 14px;
    right: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:active {
    transform: scale(.9);
  }
`

const GroupHeaderRow = styled.tr`
  display: flex;

  /* See ChargerGroups: percentage cells cannot shrink below their icon plus label, so on a phone
     they pile up. Size to content and wrap instead. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-wrap: wrap;
    gap: 0px 20px;

    > td {
      width: auto;
    }
  }
`

const GroupNameLabel = styled.p`
  font-weight: 700;
  font-size: 18px;
  color: ${props => props.theme.colors.gray_900};
  line-height: 30px;
  margin-top: 10px;
  /* A fixed share of the name cell, not flex:1 1 auto - sizing it from its own content made every
     row shrink differently at 1280, so each following column started at a different x. Truncation
     (safe here thanks to the 30px line-height) replaces the old mid-word break. */
  width: max(10vw, 128px);
  flex: 0 0 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const GroupInfoContainer = styled.td<{ $width?: string }>`
  display: flex;
  min-width: 0;
  /* No shrink: as flex items these cells shrank by different amounts depending on their content, so
     the same column started at a different x on every row and the last one was pushed under the
     card's edit button. Everything inside truncates now, so a fixed share is safe. */
  flex: 0 0 auto;
  width: ${props => props.$width ? props.$width : 'auto'};

  /* The label/value pair sits in an intermediate div. Without this the div keeps its max-content
     width, so the cell shrinking does nothing and the value still overflows into the next column. */
  > div {
    min-width: 0;
  }
`

const GroupInfoLabel = styled.p`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: 16px;
`

const GroupInfoValue = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin-top: 16px;
  ${wrapWhenNarrow}
  /* A version string is one unbreakable token in a narrow percentage cell. It must not wrap (the
     zero line-height above would paint line two on line one) and it must not overflow into the next
     column, so on desktop it truncates. Below the breakpoint the cells wrap onto their own rows and
     wrapWhenNarrow lets the token break instead. */
  @media (min-width: ${BREAKPOINTS.shell + 1}px) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* Clipping needs a real line box: with the inherited line-height: 0 the content box is zero
       tall, so overflow: hidden clips the text away completely rather than truncating it. The top
       margin is reduced by half the line height so the glyphs stay where they were. */
    line-height: 1.2;
    margin-top: 9px;
  }
`

const GroupDividerLine = styled.div`
  width: 98%;
`

/* Keeps the wide charger table inside the white card on a phone. Without the scroller the table
   simply paints past the card's right edge and its trailing columns and row menus end up floating
   on the page background. */
const GroupChargersScroller = styled.div`
  @media (max-width: ${BREAKPOINTS.shell}px) {
    overflow-x: auto;
    overscroll-behavior-x: contain;
  }
`

const GroupChargersTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: max-content;
    min-width: 100%;
  }
`

const GroupChargersTableLabel = styled.p`
  font-weight: 600;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_500};
`

const ChargerIdLabel = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin-top: 14px;
`

const ChargerEmailLabel = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: 16px;
`

const ChargerLocalNameLabel = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: 16px;
`

const ChargerSoftwareVerisonLabel = styled.p<{ $isMismatch?: boolean }>`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.$isMismatch ? props.theme.colors.danger : props.theme.colors.gray_500};
  top: 4px;
  position: absolute;
`

const ChargerTableRow = styled.tr`
  border-bottom: 1px dashed ${props => props.theme.colors.gray_300};
  position: relative;
`

const ChargerInfoRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.gray_300};
`

const ChargerStatus = styled.p<{ $status: string }>` // online / offline / disabled / charging
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.white};
  border-radius: 40px;
  width: 70px;
  line-height: 22px;
  text-align: center;
  background-color: ${props => {
    switch (props.$status) {
      case 'online':
        return props.theme.colors.success
      case 'offline':
        return props.theme.colors.gray_400
      case 'disabled':
        return props.theme.colors.gray_900
      case 'charging':
        return props.theme.colors.primary
    }
  }};
`

const ChargerSoftwareIconContainer = styled.div`
  width: 12px;
  height: 12px;
  margin-top: -4px;
  cursor: pointer;
`

const TableTimeAgoLabel = styled.p`
  font-weight: 400;
  font-size: 11px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: -4px;
  width: 100px;
  margin-left: -15px;
  text-align: center;
`

export default function Update({ charger }: { charger: ChargerType }) {
  const { t } = useTranslation()
  const { addNotification } = useNotification()
  const navigate = useNavigate()
  const chargerId = charger.chargerId

  const [groupName, setGroupName] = useState<string | null>(null)
  const [groupData, setGroupData] = useState<ChargerGroupType | null>(null)
  const [bucketFiles, setBucketFiles] = useState<{ swVersion: string[], fwVersion: string[], osVersion: string[] }>({ swVersion: [], fwVersion: [], osVersion: [] })
  const [editingSoftware, setEditingSoftware] = useState<string | null>(null)

  async function fetchData() {
    const dbRef = ref(getDatabase(fbConf))
    const response = await get(child(dbRef, 'voltie/updategroups'))
    const allGroups = response.val()

    // Find which group this charger belongs to
    let foundGroup: string | null = null
    for (const g in allGroups) {
      if (allGroups[g].chargers?.[chargerId]) {
        foundGroup = g
        break
      }
    }
    if (!foundGroup) return

    // Fetch charger details and online status
    const [detailsRes, onlineRes] = await Promise.all([
      Comm('/api-charger/chargers', {}),
      Comm('/api-websocket/online', {})
    ])
    const details: (ChargerGroupChargerType & { chargerId: string })[] = await detailsRes.json()
    const onlineChargers: string[] = await onlineRes.json()

    // Enrich only our charger
    const detail = details.find(c => c.chargerId === chargerId)
    const entry = allGroups[foundGroup].chargers[chargerId]
    if (detail && entry) {
      entry.userEmail = detail.userEmail
      entry.online = onlineChargers.includes(chargerId)
      entry.osVersionNumber = detail.osVersion
      entry.swVersionNumber = detail.swVersion
      entry.fwVersionNumber = detail.fwVersion
      entry.lastPresence = detail.lastPresence
      entry.localName = detail.localName
    }

    setGroupName(foundGroup)
    setGroupData(allGroups[foundGroup])
  }

  async function fetchBucketFiles() {
    const response = await Comm('/api-firmware/bucketfiles', {})
    setBucketFiles(await response.json())
  }

  useEffect(() => {
    fetchData()
    fetchBucketFiles()
  }, [])

  async function handleForceUpdate() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: { "Charger_id": chargerId, "Content-Type": "application/json" },
      body: JSON.stringify({ "msg_type": 15, "sub_cmd": 1 })
    })
    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  async function handleReset(fwReset = false, swReset = false, osReset = false) {
    const response = await Comm('/api-firmware/v2/update/reset', {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chargerId, fwReset, swReset, osReset })
    })
    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  function isVersionMismatch(groupFileName: string, chargerFileName: string, version: string, type: string) {
    try {
      let compareAgainst
      if (chargerFileName && chargerFileName != ' ') {
        compareAgainst = chargerFileName
      } else if (groupFileName && groupFileName !== ' ') {
        compareAgainst = groupFileName
      } else {
        return false
      }

      if (type == 'SW') {
        if (compareAgainst.split('_v')[1].split('_')[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/\.0/g, '.').replace(/\.0/g, '.') == version) {
          return false
        } else {
          return true
        }
      } else if (type == 'FW') {
        if (compareAgainst.split('_v')[1].split('.')[0].split('_')[0] == version.replace(/\./g, '')) {
          return false
        } else {
          return true
        }
      }
      return false
    } catch (err) {
      console.log(err, groupFileName, chargerFileName, version, type)
      return false
    }
  }

  async function handleEditSoftware(newSoftware: string, versionKey: VersionKey) {
    if (!groupData || !groupName) return
    const chargerEntry = groupData.chargers[chargerId]
    if (!chargerEntry || newSoftware == '' || chargerEntry[versionKey] == newSoftware) return

    if (newSoftware == 'Delete') newSoftware = ''

    try {
      await set(ref(getDatabase(fbConf), `voltie/updategroups/${groupName}/chargers/${chargerId}/${versionKey}`), newSoftware)
      await set(ref(getDatabase(fbConf), `voltie/updategroups/${groupName}/lastEdit`), Date.now())
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

    chargerEntry[versionKey] = newSoftware
    groupData.lastEdit = Date.now() as unknown as string
    setEditingSoftware(null)
    await Comm('/api-firmware/v2/update/maintain/refresh', {})
  }

  function checkIfFilePassesGroupFilter(fileName: string, filter: string): boolean {
    try {
      if (!fileName || !filter || fileName == "" || filter == "") {
        return true
      }

      // Parse filter: include='_a,_b';exclude='_c'
      const parts = Object.fromEntries(
        filter.split(";").map(p => p.trim()).filter(Boolean).map(p => {
          const [key, value] = p.split("=")
          return [key.trim(), value.replace(/^'|'$/g, "").split(",")]
        })
      )

      const include = parts.include ?? []
      const exclude = parts.exclude ?? []

      for (const inc of include) {
        if (inc && !fileName.includes(inc)) return false
      }
      for (const exc of exclude) {
        if (exc && fileName.includes(exc)) return false
      }
      return true
    } catch {
      return true
    }
  }

  if (!groupData || !groupName) return <div style={{ marginTop: '19px', marginLeft: '19px', fontSize: '19px' }}><LoaderDots /></div>

  const chargerData = groupData.chargers[chargerId]
  if (!chargerData) return <p>Charger not found in any update group</p>

  const renderSoftwareCell = (type: 'SW' | 'FW' | 'OS', versionKey: VersionKey, groupVersion: string, fwReset: boolean, swReset: boolean, osReset: boolean) => {
    const versionNumber = chargerData[`${versionKey}Number` as keyof ChargerGroupChargerType] as string
    const currentVersion = chargerData[versionKey]

    return (
      <td style={{ position: 'relative' }}>
        <div>
          <ChargerSoftwareVerisonLabel $isMismatch={isVersionMismatch(groupVersion, currentVersion, versionNumber, type)}>{versionNumber || '-'}</ChargerSoftwareVerisonLabel>
          <WhiteSpace height="22px" />
          {editingSoftware === type ? (
            <>
              <WhiteSpace height="4px" />
              <Select handleSelect={(value) => handleEditSoftware(value, versionKey)} width="max(12vw, 154px)" isAbsolute fitOptionsToContent options={[currentVersion, ...bucketFiles[versionKey].filter(s => checkIfFilePassesGroupFilter(s, groupData.frontendFilter?.[type] || "")), 'Delete']} />
            </>
          ) : (
            <div style={{ display: 'flex', position: 'relative' }}>
              {currentVersion ? (
                <>
                  <GroupInfoValue style={{ margin: '0px', marginTop: '4px' }}>{currentVersion}</GroupInfoValue>
                  <WhiteSpace width="8px" />
                  <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingSoftware(type) }}>
                    <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                  </ChargerSoftwareIconContainer>
                  <WhiteSpace width="8px" />
                  <ChargerSoftwareIconContainer title="Delete" onClick={() => handleEditSoftware('Delete', versionKey)}>
                    <Icon path="general.trash" color="danger" width="125%" height="125%" margintop="2px" />
                  </ChargerSoftwareIconContainer>
                  <WhiteSpace width="8px" />
                  <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleReset(fwReset, swReset, osReset) }}>
                    <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                  </ChargerSoftwareIconContainer>
                </>
              ) : (
                <>
                  <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingSoftware(type) }}>
                    <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                  </ChargerSoftwareIconContainer>
                  <WhiteSpace width="8px" />
                  <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleReset(fwReset, swReset, osReset) }}>
                    <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                  </ChargerSoftwareIconContainer>
                </>
              )}
            </div>
          )}
        </div>
      </td>
    )
  }

  return (
    <>
      <WhiteSpace height={'18px'} />
      <PageTitle label={t('charger_update')} />
      <WhiteSpace height={'12px'} />
      <GroupsContainer onClick={() => setEditingSoftware(null)}>

        <GroupContainer>
          <GroupHeaderTable>
            <tbody>
              <GroupHeaderRow>
                <GroupInfoContainer $width="20%">
                  <Icon path={(() => {
                    switch (groupName) {
                      case 'Installed_chargers':
                        return "chargerGroups.installed_chargersGroup"
                      case 'Betatester':
                        return "chargerGroups.betatesterGroup"
                      case 'Default':
                        return "chargerGroups.defaultGroup"
                      case 'Insider_simuhat':
                        return "chargerGroups.insider_simuhatGroup"
                      default:
                        return "chargerGroups.chargerGroup"
                    }
                  })()} width="30px" height="30px" margintop="10px" marginright="16px" />
                  <GroupNameLabel>{groupName}</GroupNameLabel>
                </GroupInfoContainer>
                <GroupInfoContainer $width="10%">
                  <div>
                    <GroupInfoLabel>{t('chargerGroups_charger')}</GroupInfoLabel>
                    <GroupInfoValue>
                      {groupData.chargers ? (
                        <>
                          {Object.keys(groupData.chargers).length}
                          <span style={{ color: '#57D9BA' }}>
                            {` (${Object.values(groupData.chargers).filter(c => c.online === true).length})`}
                          </span>
                        </>
                      ) : '0'}
                    </GroupInfoValue>
                  </div>
                </GroupInfoContainer>
                <GroupInfoContainer $width="10%">
                  <div>
                    <GroupInfoLabel style={{ marginTop: '10px' }}>{t('chargerGroups_lastEdit')}</GroupInfoLabel>
                    <GroupInfoValue>{groupData.lastEdit ? new Date(groupData.lastEdit).toString().split(' ').slice(1, 4).join(' ') : '-'}</GroupInfoValue>
                    <GroupInfoLabel>{groupData.lastEdit ? new Date(groupData.lastEdit).toString().split(' ').slice(4, 6)[0] : ''}</GroupInfoLabel>
                  </div>
                </GroupInfoContainer>
                <GroupInfoContainer $width="20%">
                  <Icon path="chargerGroups.SW" width="30px" height="30px" margintop="10px" marginright="16px" />
                  <div>
                    <GroupInfoLabel>{t('chargerGroups_defaultSoftware')}</GroupInfoLabel>
                    <GroupInfoValue>{groupData.groupSwVersion}</GroupInfoValue>
                  </div>
                </GroupInfoContainer>
                <GroupInfoContainer $width="20%">
                  <Icon path="chargerGroups.FW" width="30px" height="30px" margintop="10px" marginright="16px" />
                  <div>
                    <GroupInfoLabel>{t('chargerGroups_firmwareSystem')}</GroupInfoLabel>
                    <GroupInfoValue>{groupData.groupFwVersion}</GroupInfoValue>
                  </div>
                </GroupInfoContainer>
                <GroupInfoContainer $width="20%">
                  <Icon path="chargerGroups.OS" width="30px" height="30px" margintop="10px" marginright="16px" />
                  <div>
                    <GroupInfoLabel>{t('chargerGroups_operatingSystem')}</GroupInfoLabel>
                    <GroupInfoValue>{groupData.groupOsVersion}</GroupInfoValue>
                  </div>
                </GroupInfoContainer>
              </GroupHeaderRow>
            </tbody>
          </GroupHeaderTable>
          <GroupEditButton onClick={() => navigate(`/chargergroups?editGroup=${groupName}`)}>
            <Icon path="general.wrench" width="16px" height="16px" />
          </GroupEditButton>
          <>
            <WhiteSpace height="10px" />
            <GroupDividerLine />
            <WhiteSpace height="24px" />
            <GroupChargersScroller>
            <GroupChargersTable>
              <tbody>
                <ChargerInfoRow>
                  <td style={{ width: '30px' }}>{/* To make place for the charger icon */}</td>
                  <td style={{ width: '16%' }}>
                    <GroupChargersTableLabel>{t('chargerGroups_chargerIdUserEmailLocalName')}</GroupChargersTableLabel>
                  </td>
                  <td style={{ width: '20%' }}>
                    <GroupChargersTableLabel>{t('chargerGroups_state')}</GroupChargersTableLabel>
                  </td>
                  <td style={{ width: '20%' }}>
                    <GroupChargersTableLabel>{t('chargerGroups_defaultSoftware')}</GroupChargersTableLabel>
                  </td>
                  <td>
                    <GroupChargersTableLabel>{t('chargerGroups_firmwareSystem')}</GroupChargersTableLabel>
                  </td>
                  <td style={{ width: '19%' }}>
                    <GroupChargersTableLabel>{t('chargerGroups_operatingSystem')}</GroupChargersTableLabel>
                  </td>
                  <td style={{ width: '20px' }}>{/* To make place for the charger menu */}</td>
                </ChargerInfoRow>
                <ChargerTableRow>
                  <td>
                    <Icon path={chargerData.online ? "chargerGroups.chargerOnline" : "chargerGroups.chargerOffline"} width="30px" height="30px" marginleft="9px" marginright="16px" />
                  </td>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px' }}>
                    <div>
                      <ChargerIdLabel>{chargerId}</ChargerIdLabel>
                      <ChargerEmailLabel>{chargerData.userEmail}</ChargerEmailLabel>
                      <ChargerLocalNameLabel>{chargerData.localName != ' ' ? chargerData.localName : '-'}</ChargerLocalNameLabel>
                    </div>
                  </td>
                  <td>
                    {chargerData.online ? (
                      <ChargerStatus $status='online'>Online</ChargerStatus>
                    ) : (
                      <div>
                        <ChargerStatus $status='offline'>Offline</ChargerStatus>
                        <TableTimeAgoLabel>{dateToTimeAgoString(new Date(chargerData.lastPresence.replace(/\./g, '-')))}</TableTimeAgoLabel>
                      </div>
                    )}
                  </td>
                  {renderSoftwareCell('SW', 'swVersion', groupData.groupSwVersion, false, true, false)}
                  {renderSoftwareCell('FW', 'fwVersion', groupData.groupFwVersion, true, false, false)}
                  {renderSoftwareCell('OS', 'osVersion', groupData.groupOsVersion, false, false, true)}
                  <td>
                    <Dropdown width={'175px'} handleChoose={(value) => {
                      if (value === t('chargerGroups_forceUpdateCheck')) handleForceUpdate()
                      else if (value === `${t('chargerGroups_reactivate')} (${t('general_all')})`) handleReset(true, true, true)
                    }} title={{ icon: "general.more", label: '' }} options={[{ icon: 'general.refresh', label: t('chargerGroups_forceUpdateCheck') }, { icon: "general.error", label: `${t('chargerGroups_reactivate')} (${t('general_all')})`, color: '#FD4E4E' }]} showOptionContainerOnLeft />
                  </td>
                </ChargerTableRow>
              </tbody>
            </GroupChargersTable>
            </GroupChargersScroller>
          </>
        </GroupContainer>
      </GroupsContainer>
    </>
  )
}
